import forge from 'node-forge'
import moment from 'moment'
import { CURRENT_SUBSIDIARY, GA_USER_ID_HASH_KEY } from './constants'

declare global {
  interface Window {
    Moengage?: any
    dataLayer?: any[]
  }
}

// Keys carrying PII; stripped from GA4 (Google policy) but kept for MoEngage.
const GA_PII_KEYS = new Set([
  'u_mb',
  'u_n',
  'pData',
  'email',
  'mobile',
  'phone',
  'name',
])

function sanitizeGaParams(payload?: Record<string, any>) {
  if (!payload) return payload
  const sanitized: Record<string, any> = {}
  Object.keys(payload).forEach((key) => {
    if (!GA_PII_KEYS.has(key)) sanitized[key] = payload[key]
  })
  return sanitized
}

// Phone space is small, so plain hash is brute-forceable; HMAC with a secret salt isn't.
// Returns '' when the salt is missing rather than letting forge throw on an undefined key:
// this runs from MoengageSyncProfile's effect, which sits in the root layout, so a throw
// escalates to global-error.tsx and takes out every page. Losing GA user stitching is the
// cheaper failure. scripts/check-injected-env.mjs keeps the key from going missing.
export function hashGaUserId(phoneNumber: string): string {
  if (!GA_USER_ID_HASH_KEY) {
    console.warn('GA user id hash key missing; skipping GA user_id')
    return ''
  }
  const normalized = phoneNumber.replace(/\D/g, '').replace(/^0+/, '')
  const hmac = forge.hmac.create()
  hmac.start('sha256', GA_USER_ID_HASH_KEY)
  hmac.update(normalized)
  return hmac.digest().toHex()
}

/**
 * Push the same event to GTM/GA via dataLayer, stripped of PII
 */
function pushToDataLayer(eventName: string, payload?: Record<string, any>) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: eventName, ...sanitizeGaParams(payload) })
}

export interface GaEcommerceItem {
  item_id: string
  item_name: string
  price?: number
  quantity?: number
  item_category?: string
  item_brand?: string
  [key: string]: any
}

export interface GaEcommercePayload {
  transaction_id: string
  value: number
  currency?: string
  coupon?: string
  shipping?: number
  tax?: number
  items: GaEcommerceItem[]
}

// Single GA entry point for components: pass an event name + payload, same shape as trackEvent.
// Ecommerce payloads (anything with `items`) get auto-wrapped in `ecommerce` and null-cleared first, per GA4/GTM's schema.
export function trackGaEvent(
  eventName: string,
  payload?: Record<string, any> | GaEcommercePayload
) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  if (payload && 'items' in payload) {
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({ event: eventName, ecommerce: payload })
    return
  }
  window.dataLayer.push({ event: eventName, ...sanitizeGaParams(payload) })
}

/**
 * Check if MoEngage is available
 */
export const getMoengage = (): any | null => {
  if (typeof window !== 'undefined' && window.Moengage) {
    return window.Moengage
  }
  console.warn('[MoEngage] SDK not available.')
  return null
}

/**
 * Track a MoEngage event safely
 */
export function trackEvent(eventName: string, payload?: Record<string, any>) {
  if (CURRENT_SUBSIDIARY === 'DDRC') return // MoEngage events are disabled for DDRC
  const Moengage = getMoengage()
  if (Moengage && typeof Moengage.track_event === 'function') {
    Moengage.track_event(eventName, payload || {})
  }
  pushToDataLayer(eventName, payload)
}

/**
 * Update user profile attributes
 */
export const updateEventUser = ({
  firstName,
  lastName,
  email,
  dob,
  phoneNumber,
  gender,
  code,
}: {
  firstName: string
  lastName: string
  email?: string
  dob?: string
  phoneNumber: string
  gender?: string
  code?: string
}) => {
  if (CURRENT_SUBSIDIARY === 'DDRC') return
  const Moengage = getMoengage()
  if (!Moengage) return

  if (firstName) Moengage.add_first_name(firstName)
  if (lastName) Moengage.add_last_name(lastName)
  if (email) Moengage.add_email(email)
  if (phoneNumber) Moengage.add_mobile(phoneNumber)
  if (firstName || lastName)
    Moengage.add_user_name(`${firstName || ''} ${lastName || ''}`)
  if (gender) Moengage.add_gender(gender)
  if (dob) Moengage.add_birthday(moment(dob).format('YYYY-MM-DD'))
  if (phoneNumber) Moengage.add_unique_user_id(phoneNumber)

  // GA4 user_id must not be raw PII; send a keyed hash of the phone.
  if (phoneNumber && typeof window !== 'undefined') {
    const gaUserId = hashGaUserId(phoneNumber)
    // An empty user_id would overwrite a good one from an earlier push, so skip it.
    if (gaUserId) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ user_id: gaUserId })
    }
  }
}

export const eventConstants = {
  LEAD_FORM: 'lead_form',
  REQUEST_CALL_BACK: 'request_call_back',
  SUBMIT_PRESCRIPTION: 'submit_prescription',
  BOOK_VIA_WHATSAPP: 'book_via_whatsapp',
  CALL_CUSTOMER_CARE: 'call_customer_care',
  ADD_TO_CART: 'add_to_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  APPLY_COUPON: 'apply_coupon',
  REDEEM_LOYALTY_POINTS: 'redeem_loyalty_points',
  PROCEED_TO_PAY: 'proceed_to_pay',
  PAY_ONLINE: 'pay_online',
  PAY_COD: 'pay_cod',
  NEARBY_LABS: 'nearby_labs',
  SUBMIT_NEARBY_LABS_LEAD: 'submit_nearby_labs_lead',
  ORDER_CONFIRMED: 'order_confirmed',
  MY_REPORTS: 'my_reports',
  PSYNC: 'psync',
}
